<script>
  import "vite/dynamic-import-polyfill";
  import { Badges, Footer, Newsletters } from "./shared";
  import mail_icon_src from "./public_profile/images/mail-icon.svg";
  import views_icon_src from "./public_profile/images/views-icon.svg";
  import { formatNumber } from "../ui/numberUtils";
  import { setFolderPermissions } from "./shared";
  import CopyToClipboard from "@ui/CopyToClipboard.svelte";

  export let user;
  export let userBadges;
  export let notUserBadges;
  export let newsletters;
  export let totalNewslettersViews;
  export let viewedAsOrgAdminOrOwner;
  export let isGroupAdmin;
  export let orgName;
  export let isPreview;
  export let publicFolder;
  export let myNewslettersUrl;
  export let publicProfileUrl;

  $: newsletterList = groupNewsletters(newsletters);
  setFolderPermissions({
    folderId: "public_newsletters",
    can_delete_newsletters: false,
    can_remove_newsletters: false,
    can_edit_newsletters: false
  });

  function groupNewsletters(newsletterItems) {
    const groupsByName = {};

    // Formats the groups as: "[long month] [4-digit year]" (e.g. "September 2020")
    const dateFormatForGroupName = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });

    // Sorts the newsletter by their "last created" date.
    // This date is formatted in the server as ISO8601 (e.g. "2020-11-23T10:37:46+00:00") so no problem sorting it here.
    const sortedNewsletterItems = newsletterItems.sort((n1, n2) => {
      if (n1.timestamps.lastUpdated > n2.timestamps.lastUpdated) {
        return -1;
      }
      if (n1.timestamps.lastUpdated < n2.timestamps.lastUpdated) {
        return 1;
      }
      return 0;
    });

    sortedNewsletterItems.forEach((n) => {
      const lastSavedAtDate = new Date(n.timestamps.lastUpdated);
      const groupName = dateFormatForGroupName.format(lastSavedAtDate);

      if (!groupsByName[groupName]) {
        groupsByName[groupName] = {
          name: groupName,
          items: []
        };
      }

      groupsByName[groupName].items.push(n);
    });

    return Object.values(groupsByName);
  }
</script>

<div class="flex flex-col justify-between min-h-screen">
  <div class="flex flex-col items-center">
    <div class="w-full bg-teal-300 opacity-50" style="height: 10px;" />
    <div class="w-full container-1200">
      {#if isPreview || viewedAsOrgAdminOrOwner || isGroupAdmin}
        <div class="p-4 mt-6 notice-bar">
          {#if isPreview}
            <h1>This is your public profile, only public newsletters are viewable to visitors.</h1>
            Remove newsletters from your public profile by making newsletters private.
            <a href="https://help.smore.com/article/1277-your-public-smore-profile" class="absolute top-0 right-0 hidden p-4 md:block"
              ><h1 class="text-teal-600">Learn more</h1></a>
          {:else if viewedAsOrgAdminOrOwner}
            <h1>{user.name} is a member of {orgName}.</h1>
            As an admin, you can see all of their public and private newsletters. However, visitors to their public profile will only see newsletters
            listed as private.
          {:else if isGroupAdmin}
            <h1>{user.name} is a member of a group you manage.</h1>
            As an admin, you can see all of their public and private newsletters. However, visitors to their public profile will only see newsletters
            listed as private.
          {/if}
        </div>
      {/if}

      <div class="box-border flex flex-col items-start justify-start mt-8 lg:flex-row">
        <div class="hidden sidebar lg:block">
          <div
            class="flex-shrink-0 w-16 h-16 bg-gray-100 bg-center bg-no-repeat bg-cover border rounded-full"
            style="background-image: url({user.picture})"
            role="img"
            aria-label="your profile avatar" />
          <div class="mt-3 font-bold user-name">{user.name}</div>
          <div class="text-base text-brand-gray-70">{user.bio}</div>
          <div class="flex items-center justify-between mt-5">
            <div>
              <img src={mail_icon_src} alt="newsletters" />
              <div class="mt-2 text-base">{formatNumber(newsletters.length)} {newsletters.length == 1 ? "newsletter" : "newsletters"}</div>
            </div>
            <div>
              <img src={views_icon_src} alt="number of views" />
              <div class="mt-2 text-base">{formatNumber(totalNewslettersViews)} total {totalNewslettersViews == 1 ? "view" : "views"}</div>
            </div>
          </div>
          <!-- <div class="py-2 mt-5 text-sm text-center text-white bg-blue-600 rounded sm-clickable hover:bg-blue-500">Contact {user.name}</div> -->
          <div class="w-full mt-8">
            <Badges hideDescription myBadges={userBadges} notMyBadges={notUserBadges} badgeSize="medium" />
          </div>
        </div>
        <div class="flex flex-col items-start justify-center w-full ml-0 newsletters-container lg:ml-16">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center justify-start">
              <h2 class="text-lg font-bold">
                {#if publicFolder}{publicFolder.name}{:else}{user.name}'s Newsletters{/if}
              </h2>
              <div class="ml-2 text-base text-brand-gray-70">({formatNumber(newsletters.length)})</div>
            </div>
            {#if isPreview}
              <div class="flex items-center justify-start">
                <a href={myNewslettersUrl} class="mr-6 text-sm font-normal sm-clickable text-brand-gray">Back to Newsletters</a>
                <div
                  class="px-3 py-2 font-medium text-white truncate bg-blue-600 border border-blue-600 rounded shadow sm-clickable text-14 hover:border-blue-500 hover:bg-blue-500">
                  <div class="flex items-center">
                    <CopyToClipboard textToCopy={publicProfileUrl}>
                      <span class="flex items-center font-medium text-white">
                        <i class="mr-2 opacity-50 material-icons md-18">file_copy</i> Copy Profile Link
                      </span>
                    </CopyToClipboard>
                  </div>
                </div>
              </div>
            {/if}
          </div>
          <div class="w-full mt-2">
            {#each newsletterList as group, groupIndex (group.name)}
              <div class="flex items-center justify-between w-full">
                <h4 class="mt-2 mb-2 font-light text-md text-brand-gray-70">{group.name}</h4>
              </div>
              <div class="w-full mt-2">
                <Newsletters newsletters={group.items} hideOverlay />
              </div>
            {:else}No newsletters found{/each}
          </div>
        </div>
      </div>
    </div>
  </div>

  <Footer />
</div>

<style>
  .sidebar {
    width: 300px;
  }
  .newsletters-container {
  }
  .user-name {
    font-size: 26px;
  }
  .notice-bar {
    position: relative;
    width: 100%;
    background-color: #fff7e2;
    border-radius: 4px;
    border: 1px solid #dddddd;
    box-sizing: border-box;
    font-size: 13px;
    text-align: center;
  }
  .notice-bar h1 {
    font-weight: 500;
    font-size: 15px;
  }
</style>
